/**
 * Ledger table for /billing/family-balance (Plan 09).
 *
 * Paginated history of money movements against the wallet's
 * `balanceCents`. Three columns + a sign-prefixed amount + a
 * "Balance after" running total snapshot. Kind filter chips at the
 * top + a "Download CSV" menu at the right, which exports either the
 * chip-filtered rows or the whole history (PROD-1135).
 *
 * v1 paginates client-side at 20 rows per page; server pagination is
 * a follow-up when accounts grow beyond ~500 ledger entries. The
 * underlying GraphQL connection IS cursor-paginated so the cutover is
 * mechanical.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ordo/ui/dropdown-menu'
import { FilterChipGroup } from '@ordo/ui/filter-chip-group'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { ChevronDown, Download } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { BillingLedgerEntry } from '@/api/billing-family'
import { humanRefundCause, type RefundCauseCode } from '@/components/refund/refund-cause-picker-helpers'
import { RefundStatusChip } from '@/components/refund/refund-status-chip'
import { isPendingClearance, PENDING_CLEARANCE_HINT } from '@/lib/top-up-polling'

import {
  downloadCsv,
  expandKindGroups,
  filterLedgerEntries,
  formatCents,
  formatLedgerAmount,
  formatLedgerTimestamp,
  LEDGER_KIND_GROUPS,
  ledgerCsvFilename,
  ledgerCsvFilterScopeLabel,
  ledgerEntriesToCsv,
  ledgerKindBadgeVariant,
  ledgerKindLabel,
  paginateLedgerEntries,
  parseRefundDescription,
} from './helpers'

const KIND_OPTIONS = LEDGER_KIND_GROUPS

const PAGE_SIZE = 20

/**
 * The account slice the table actually reads. `BillingFamilyAccount`
 * satisfies it structurally; the school Balance view passes a lean
 * adapter built from the `/api/billing-school/account/ledger` payload.
 */
export interface LedgerTableAccount {
  currency: string
  ledger: { edges: Array<{ node: BillingLedgerEntry }> }
}

export interface LedgerTableProps {
  account: LedgerTableAccount
  /**
   * The word for the account in the header/empty-state copy — "wallet"
   * (family default) or "account" (the school org balance).
   */
  accountNoun?: 'wallet' | 'account'
}

export function LedgerTable({ account, accountNoun = 'wallet' }: LedgerTableProps) {
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(0)

  const selectedKinds = useMemo(() => expandKindGroups(selectedGroups), [selectedGroups])
  const allEntries = useMemo(() => account.ledger.edges.map((e) => e.node), [account.ledger.edges])
  const filtered = useMemo(() => filterLedgerEntries(allEntries, selectedKinds), [allEntries, selectedKinds])
  const pageData = useMemo(() => paginateLedgerEntries(filtered, page, PAGE_SIZE), [filtered, page])

  // ponytail: "all" means every row the page LOADED — both feeds cap at 200
  // (`first=200`, server clamp 500). Real all-history needs the server pagination
  // this component's header already defers; no reachable account is truncated today.
  const handleDownload = (scope: 'filtered' | 'all') => {
    const csv = ledgerEntriesToCsv(scope === 'filtered' ? filtered : allEntries)
    const stamp = new Date().toISOString().slice(0, 10)
    downloadCsv(ledgerCsvFilename(stamp, scope), csv)
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-lg font-semibold tracking-tight">Billing history</h3>
            <p className="text-xs text-muted-foreground">
              {selectedGroups.size > 0
                ? `${pageData.totalEntries} of ${allEntries.length} entries shown.`
                : `${allEntries.length} ${allEntries.length === 1 ? 'entry' : 'entries'} on this ${accountNoun}.`}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={allEntries.length === 0}>
                <Download className="h-4 w-4" aria-hidden />
                Download CSV
                <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => handleDownload('filtered')} disabled={filtered.length === 0}>
                {ledgerCsvFilterScopeLabel(selectedGroups.size)}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => handleDownload('all')}>All data</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <FilterChipGroup
          label="Filter by type"
          options={KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          selected={selectedGroups}
          onChange={(next) => {
            setSelectedGroups(next)
            setPage(0)
          }}
        />
      </CardHeader>
      <CardContent>
        {pageData.entries.length === 0 ? (
          <EmptyState filtered={selectedKinds.size > 0} accountNoun={accountNoun} />
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance after</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageData.entries.map((entry) => (
                  <LedgerRow key={entry.id} entry={entry} currency={account.currency} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {pageData.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Page {pageData.page + 1} of {pageData.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={pageData.page === 0}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pageData.totalPages - 1, p + 1))}
                disabled={pageData.page >= pageData.totalPages - 1}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function LedgerRow({ entry, currency }: { entry: BillingLedgerEntry; currency: string }) {
  // Plan 44 — surface the refund cause + status chip alongside REFUND
  // entries so parents see "Refunded — Vendor quality" not "Refund credit."
  const refundMeta = entry.kind === 'REFUND' ? parseRefundDescription(entry.description) : null
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col items-start gap-1">
          <Badge variant={ledgerKindBadgeVariant(entry.kind)}>{ledgerKindLabel(entry.kind)}</Badge>
          {refundMeta && (
            <RefundStatusChip status="refunded" title={humanRefundCause(refundMeta.cause as RefundCauseCode)} />
          )}
          {isPendingClearance(entry) && (
            <Badge variant="secondary" title={PENDING_CLEARANCE_HINT}>
              Clearing
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {formatLedgerTimestamp(entry.occurredAt)}
      </TableCell>
      <TableCell className="text-sm">
        {refundMeta ? (
          <div className="flex flex-col gap-0.5">
            <span>{humanRefundCause(refundMeta.cause as RefundCauseCode)}</span>
            {refundMeta.note && <span className="text-xs text-muted-foreground">{refundMeta.note}</span>}
          </div>
        ) : (
          (entry.description ?? defaultDescription(entry))
        )}
      </TableCell>
      <TableCell className="text-right font-medium tabular-nums">
        {formatLedgerAmount(entry.amountCents, entry.currency || currency)}
      </TableCell>
      <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
        {formatCents(entry.balanceAfter, entry.currency || currency)}
      </TableCell>
    </TableRow>
  )
}

function defaultDescription(entry: BillingLedgerEntry): string {
  switch (entry.kind) {
    case 'TOPUP':
      return entry.stripeIntentId ? 'Funds added via Stripe' : 'Funds added'
    case 'CHARGE':
      return entry.orderBatchId ? `Order ${entry.orderBatchId.slice(0, 6)}` : 'Order debit'
    case 'REFUND':
      return entry.orderBatchId ? `Refund for order ${entry.orderBatchId.slice(0, 6)}` : 'Refund credit'
    case 'ADJUSTMENT':
      return 'Manual adjustment'
    case 'TOPUP_REVERSAL':
      return 'Added funds refunded to your card'
    case 'TOPUP_REVERSAL_VOID':
      return 'Card refund canceled — funds restored'
    case 'DISPUTE_HOLD':
      return 'Payment disputed — funds on hold'
    case 'DISPUTE_RELEASE':
      return 'Dispute resolved in your favor'
    case 'TOPUP_CLAWBACK':
      return 'Bank transfer did not clear — funds removed'
  }
}

function EmptyState({ filtered, accountNoun }: { filtered: boolean; accountNoun: 'wallet' | 'account' }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm">
      <p className="font-medium text-foreground">{filtered ? 'No entries match your filter' : 'No transactions yet'}</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        {filtered
          ? 'Try clearing the type filter to see the full history.'
          : `Your ${accountNoun} activity (added funds, orders, refunds) will show up here.`}
      </p>
    </div>
  )
}
